import { useEffect, useMemo, useState } from 'react';
import type { DataTableFilterField } from '@r/types/index.ts';
import {
  getCoreRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type ColumnFiltersState,
  type PaginationState,
  type SortingState,
  type VisibilityState
} from '@tanstack/react-table';

import { useDebounce } from '@r/hooks/use-debounce.ts';
import { useLocalStorage } from '@r/hooks/use-local-storage.ts';

interface DataTablePersistenceOptions {
  /**
   * Storage key used to persist table state in localStorage.
   * Example: admin:table:v1:companies:user-42
   */
  key: string;
  /**
   * Expiration delay in milliseconds.
   * @default 30 days
   */
  ttlMs?: number;
  /**
   * Delay before writing in localStorage.
   * @default 300ms
   */
  debounceMs?: number;
}

interface PersistedDataTableState {
  // Version du payload persiste pour faciliter les evolutions futures.
  version: 1;
  // Horodatage d'ecriture, utilise pour l'expiration (TTL).
  updatedAt: number;
  pagination: PaginationState;
  sorting: SortingState;
  columnFilters: ColumnFiltersState;
  columnVisibility?: VisibilityState;
}

interface UseDataTableProps<TData, TValue> {
  /**
   * The data for the table.
   * @default []
   * @type TData[]
   */
  data: TData[];

  /**
   * The columns of the table.
   * @default []
   * @type ColumnDef<TData, TValue>[]
   */
  columns: ColumnDef<TData, TValue>[];

  /**
   * The number of pages in the table.
   * @type number
   */
  pageCount?: number;

  /**
   * Defines filter fields for the table. Supports both dynamic faceted filters and search filters.
   * - Faceted filters are rendered when `options` are provided for a filter field.
   * - Otherwise, search filters are rendered.
   *
   * The indie filter field `value` represents the corresponding column name in the database table.
   * @default []
   * @type { label: string, value: keyof TData, placeholder?: string, options?: { label: string, value: string, icon?: ComponentType<{ className?: string }> }[] }[]
   * @example
   * ```ts
   * // Render a search filter
   * const filterFields = [
   *   { label: "Title", value: "title", placeholder: "Search titles" }
   * ];
   * // Render a faceted filter
   * const filterFields = [
   *   {
   *     label: "Status",
   *     value: "status",
   *     options: [
   *       { label: "Todo", value: "todo" },
   *       { label: "In Progress", value: "in-progress" },
   *       { label: "Done", value: "done" },
   *       { label: "Canceled", value: "canceled" }
   *     ]
   *   }
   * ];
   * ```
   */
  filterFields?: DataTableFilterField<TData>[];

  /**
   * Enable notion like column filters.
   * Advanced filters and column filters cannot be used at the same time.
   * @default false
   * @type boolean
   */
  enableAdvancedFilter?: boolean;

  /**
   * Enable manual pagination.
   * @default true
   * @type boolean
   */
  manualPaginate?: boolean;

  /**
   * Enable manual sorting.
   * @default true
   * @type boolean
   */
  manualSort?: boolean;

  /**
   * Enable manual filtering.
   * @default true
   * @type boolean
   */
  manualFilter?: boolean;

  /**
   * Persist pagination, sorting and filters in localStorage.
   * @default undefined
   */
  persistence?: DataTablePersistenceOptions;
  initialColumnVisibility?: VisibilityState;
}

const DEFAULT_PERSISTENCE_TTL_MS = 30 * 24 * 60 * 60 * 1000;
const DEFAULT_PERSISTENCE_DEBOUNCE_MS = 300;

// Validation runtime du payload restaure pour eviter des crashs
// si la structure stockee change ou est corrompue.
function isValidPaginationState(value: unknown): value is PaginationState {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as Partial<PaginationState>;
  return (
    Number.isInteger(candidate.pageIndex) &&
    Number.isInteger(candidate.pageSize) &&
    (candidate.pageIndex ?? -1) >= 0 &&
    (candidate.pageSize ?? 0) > 0
  );
}

function isValidSortingState(value: unknown): value is SortingState {
  if (!Array.isArray(value)) return false;
  return value.every((item) => {
    if (!item || typeof item !== 'object') return false;
    const candidate = item as { id?: unknown; desc?: unknown };
    return typeof candidate.id === 'string' && typeof candidate.desc === 'boolean';
  });
}

function isValidColumnFiltersState(value: unknown): value is ColumnFiltersState {
  if (!Array.isArray(value)) return false;
  return value.every((item) => {
    if (!item || typeof item !== 'object') return false;
    const candidate = item as { id?: unknown };
    return typeof candidate.id === 'string';
  });
}

function isValidVisibilityState(value: unknown): value is VisibilityState {
  if (value === undefined) return true;
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  return Object.values(value as Record<string, unknown>).every(
    (entry) => typeof entry === 'boolean'
  );
}

function isValidPersistedDataTableState(value: unknown): value is PersistedDataTableState {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as Partial<PersistedDataTableState>;
  return (
    candidate.version === 1 &&
    typeof candidate.updatedAt === 'number' &&
    isValidPaginationState(candidate.pagination) &&
    isValidSortingState(candidate.sorting) &&
    isValidColumnFiltersState(candidate.columnFilters) &&
    isValidVisibilityState(candidate.columnVisibility)
  );
}

function sanitizeColumnFilters(columnFilters: ColumnFiltersState): ColumnFiltersState {
  return columnFilters.filter((filter) => {
    if (filter.value === undefined || filter.value === null) return false;
    if (typeof filter.value === 'string') return filter.value.trim().length > 0;
    if (Array.isArray(filter.value)) return filter.value.length > 0;
    return true;
  });
}

function getPersistedState(
  persistence: DataTablePersistenceOptions | undefined,
  getJsonItem: <T>(key: string) => T | null,
  removeItem: (key: string) => void
): PersistedDataTableState | null {
  if (!persistence?.key) return null;

  // Lecture via le hook centralise (avec parse JSON safe).
  const parsed = getJsonItem<unknown>(persistence.key);
  if (!parsed) return null;
  // Protection: on ignore les payloads invalides/incompatibles.
  if (!isValidPersistedDataTableState(parsed)) return null;

  const ttlMs = persistence.ttlMs ?? DEFAULT_PERSISTENCE_TTL_MS;
  // Nettoyage automatique des anciens états pour eviter des filtres obsoletes.
  if (Date.now() - parsed.updatedAt > ttlMs) {
    removeItem(persistence.key);
    return null;
  }

  return {
    ...parsed,
    columnFilters: sanitizeColumnFilters(parsed.columnFilters)
  };
}

const EMPTY_FILTER_FIELDS: DataTableFilterField<unknown>[] = [];

export function useDataTable<TData, TValue>({
  data,
  columns,
  pageCount,
  filterFields = EMPTY_FILTER_FIELDS as DataTableFilterField<TData>[],
  enableAdvancedFilter = true,
  manualPaginate = true,
  manualSort = true,
  manualFilter = true,
  persistence,
  initialColumnVisibility
}: UseDataTableProps<TData, TValue>) {
  // Point d'entree unique pour les acces localStorage.
  // Permet de partager la meme logique partout dans le projet.
  const { getJsonItem, setJsonItem, removeItem } = useLocalStorage();
  const persistenceKey = persistence?.key;
  const persistenceTtlMs = persistence?.ttlMs;
  const persistenceDebounceMs = persistence?.debounceMs;
  const persistenceOptions = useMemo(
    () =>
      persistenceKey
        ? { key: persistenceKey, ttlMs: persistenceTtlMs, debounceMs: persistenceDebounceMs }
        : undefined,
    [persistenceDebounceMs, persistenceKey, persistenceTtlMs]
  );

  // Lecture initiale du localStorage (si active), utilisee pour hydrater l'etat de depart.
  const persistedState = useMemo(
    () => getPersistedState(persistenceOptions, getJsonItem, removeItem),
    [persistenceOptions, getJsonItem, removeItem]
  );

  // Memoize computation of searchableColumns and filterableColumns
  const { searchableColumns, filterableColumns } = useMemo(() => {
    return {
      searchableColumns: filterFields.filter((field) => !field.options),
      filterableColumns: filterFields.filter((field) => field.options)
    };
  }, [filterFields]);

  // Table states
  const [rowSelection, setRowSelection] = useState({});
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({
    // Colonnes techniques cachees par defaut.
    isCustom: false,
    search: false,
    ...initialColumnVisibility,
    ...(persistedState?.columnVisibility ?? {})
  });
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(
    persistedState?.columnFilters ?? []
  );

  // Handle server-side pagination
  const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>(
    persistedState?.pagination ?? {
      pageIndex: 0,
      pageSize: 10
    }
  );

  const pagination = useMemo(
    () => ({
      pageIndex,
      pageSize
    }),
    [pageIndex, pageSize]
  );

  // Handle server-side sorting
  const [sorting, setSorting] = useState<SortingState>(persistedState?.sorting ?? []);

  // Handle server-side filtering
  const debouncedSearchableColumnFilters = JSON.parse(
    useDebounce(
      JSON.stringify(
        columnFilters.filter((filter) => {
          return searchableColumns.find((column) => column.value === filter.id);
        })
      ),
      500
    )
  ) as ColumnFiltersState;

  const filterableColumnFilters = columnFilters.filter((filter) => {
    return filterableColumns.find((column) => column.value === filter.id);
  });

  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    // Opt out when advanced filter is enabled, because it contains additional params
    if (enableAdvancedFilter) return;
    // Prevent resetting the page on initial render
    if (!mounted) {
      setMounted(true);
      return;
    }

    // Initialize new params
    const newParamsObject: ColumnFiltersState = [];

    // Handle debounced searchable column filters
    for (const column of debouncedSearchableColumnFilters) {
      if (typeof column.value === 'string') {
        Object.assign(newParamsObject, {
          [column.id]: column.value
        });
      }
    }

    // Handle filterable column filters
    for (const column of filterableColumnFilters) {
      if (typeof column.value === 'object' && Array.isArray(column.value)) {
        Object.assign(newParamsObject, { [column.id]: column.value.join('.') });
      }
    }
  }, [debouncedSearchableColumnFilters, filterableColumnFilters]);

  useEffect(() => {
    if (!persistenceKey) return;
    const restoredState = getPersistedState(persistenceOptions, getJsonItem, removeItem);
    if (!restoredState) return;

    // Rehydratation au montage/changement de cle:
    // pagination + tri + filtres + colonnes reappliques tels qu'ils etaient en localStorage.
    setPagination(restoredState.pagination);
    setSorting(restoredState.sorting);
    setColumnFilters(restoredState.columnFilters);
    if (restoredState.columnVisibility) {
      setColumnVisibility({
        isCustom: false,
        search: false,
        ...initialColumnVisibility,
        ...restoredState.columnVisibility
      });
    }
  }, [persistenceKey, persistenceOptions, getJsonItem, removeItem, initialColumnVisibility]);

  useEffect(() => {
    if (!persistenceKey) return;

    // Debounce ecriture: evite de surcharger localStorage pendant la saisie.
    const debounceMs = persistenceDebounceMs ?? DEFAULT_PERSISTENCE_DEBOUNCE_MS;
    const timeout = window.setTimeout(() => {
      // Sauvegarde minimale necessaire pour restaurer le contexte utilisateur
      // (navigation de table + tri + filtres), sans stocker les donnees de lignes.
      const sanitizedColumnFilters = sanitizeColumnFilters(columnFilters);
      const stateToPersist: PersistedDataTableState = {
        version: 1,
        updatedAt: Date.now(),
        pagination,
        sorting,
        columnFilters: sanitizedColumnFilters,
        columnVisibility
      };

      setJsonItem(persistenceKey, stateToPersist);
    }, debounceMs);

    return () => window.clearTimeout(timeout);
  }, [
    persistenceKey,
    persistenceDebounceMs,
    pagination,
    sorting,
    columnFilters,
    columnVisibility,
    setJsonItem
  ]);

  function resetToFirstPage() {
    // Regle UX globale: nouveau tri/filtre => retour page 1.
    setPagination((prev) => {
      if (prev.pageIndex === 0) return prev;
      return {
        ...prev,
        pageIndex: 0
      };
    });
  }

  function onSortingChange(nextSorting: SortingState | ((old: SortingState) => SortingState)) {
    // UX: un changement de tri repart de la premiere page pour eviter les resultats vides.
    setSorting(nextSorting);
    resetToFirstPage();
  }

  function onColumnFiltersChange(
    nextFilters: ColumnFiltersState | ((old: ColumnFiltersState) => ColumnFiltersState)
  ) {
    // UX: un changement de filtre repart de la premiere page.
    setColumnFilters(nextFilters);
    resetToFirstPage();
  }

  const table = useReactTable({
    data,
    columns,
    pageCount: pageCount ?? Math.ceil(data.length / pageSize),
    state: {
      pagination,
      sorting,
      columnVisibility,
      rowSelection,
      columnFilters
    },
    enableRowSelection: true,
    onRowSelectionChange: setRowSelection,
    onPaginationChange: setPagination,
    onSortingChange,
    onColumnFiltersChange,
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    manualPagination: manualPaginate,
    manualSorting: manualSort,
    manualFiltering: manualFilter
  });

  return { table };
}
